import { PlanControlSlot } from '../upgrade/UpgradeExperience';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  FiAlertCircle,
  FiArrowDown,
  FiArrowLeft,
  FiArrowRight,
  FiArrowUp,
  FiClock,
  FiFileText,
  FiMonitor,
  FiPlus,
  FiRefreshCw,
  FiSend,
  FiTarget,
  FiTrash2,
  FiUpload,
} from 'react-icons/fi';
import {
  answerLearningAssessmentDefense,
  getLearningAssessment,
  saveLearningAssessmentDraft,
  startLearningAssessmentDefense,
  submitGuidedPractice,
  submitWorkSample,
  uploadWorkSamplePdf,
} from '../learningPathwaysApi';
import { useLearningCopy } from './learningLocale';
import { useLanguage } from '../contexts/LanguageContext';

const PROCESSING_STATUSES = new Set(['preparing', 'evaluating', 'evaluating_artifact', 'evaluating_final']);

function lines(value) {
  if (Array.isArray(value)) return value;
  return String(value || '').split('\n').map((item) => item.trim()).filter(Boolean);
}

function clock(seconds) {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60).toString().padStart(2, '0');
  const remainder = (safe % 60).toString().padStart(2, '0');
  return `${minutes}:${remainder}`;
}

function statusLabel(status, copy) {
  if (status === 'Demonstrated') return copy('Passed');
  if (status === 'Needs revision') return copy('Needs changes');
  return copy('Could not review');
}

function hasAssessmentResponse(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value).length > 0;
  return String(value || '').trim().length > 0;
}

function readableMaterialBlocks(value) {
  const text = String(value || '').replace(/\r\n?/g, '\n').trim();
  if (!text) return [];
  const explicitBlocks = text.split(/\n\s*\n/).map((item) => item.trim()).filter(Boolean);
  if (explicitBlocks.length > 1) return explicitBlocks;

  const lines = text.split('\n').map((item) => item.trim()).filter(Boolean);
  if (lines.length > 1) return lines;

  const sentences = text.match(/[^.!?]+(?:[.!?]+|$)/g)?.map((item) => item.trim()).filter(Boolean) || [text];
  const blocks = [];
  let current = '';
  sentences.forEach((sentence) => {
    if (current && `${current} ${sentence}`.length > 190) {
      blocks.push(current);
      current = sentence;
    } else {
      current = current ? `${current} ${sentence}` : sentence;
    }
  });
  if (current) blocks.push(current);
  return blocks;
}

function MaterialContent({ content }) {
  return (
    <div className="js-la-material-copy">
      {readableMaterialBlocks(content).map((block, index) => <p key={`${index}-${block.slice(0, 24)}`}>{block}</p>)}
    </div>
  );
}

function responseMinimum(field) {
  const floor = { short_text: 80, long_text: 250, code: 300 }[field.type];
  const storedMinimum = Number(field.minLength || 0);
  return floor ? Math.min(storedMinimum || floor, floor) : storedMinimum;
}

function responseItemMinimum(field) {
  const storedMinimum = Number(field.minItems || 0);
  return storedMinimum ? Math.min(storedMinimum, 2) : 0;
}

function RankingField({ field, value, onChange }) {
  const copy = useLearningCopy();
  const items = Array.isArray(value) && value.length ? value : (field.options || []);
  const move = (index, offset) => {
    const nextIndex = index + offset;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const next = [...items];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    onChange(next);
  };
  return (
    <div className="js-la-ranking">
      {items.map((item, index) => (
        <div key={`${item}-${index}`}>
          <strong>{index + 1}</strong>
          <span>{item}</span>
          <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label={copy('Move {item} up', { item })}>
            <FiArrowUp aria-hidden="true" />
          </button>
          <button type="button" onClick={() => move(index, 1)} disabled={index === items.length - 1} aria-label={copy('Move {item} down', { item })}>
            <FiArrowDown aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
}

function TableField({ field, value, onChange }) {
  const copy = useLearningCopy();
  const columns = Array.isArray(field.columns) && field.columns.length ? field.columns : ['Item', 'Notes'];
  const rows = Array.isArray(value) && value.length ? value : [{}];
  const updateCell = (index, column, cell) => {
    const next = rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [column]: cell } : row));
    onChange(next);
  };
  return (
    <div className="js-la-table-wrap">
      <table>
        <thead><tr>{columns.map((column) => <th key={column}>{column}</th>)}<th aria-label={copy('Row actions')} /></tr></thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`row-${index}`}>
              {columns.map((column) => (
                <td key={column}>
                  <input value={row?.[column] || ''} onChange={(event) => updateCell(index, column, event.target.value)} />
                </td>
              ))}
              <td>
                <button type="button" onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))} aria-label={copy('Remove row')}>
                  <FiTrash2 aria-hidden="true" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="js-la-add-row" onClick={() => onChange([...rows, {}])}>
        <FiPlus aria-hidden="true" />
        <span>{copy('Add row')}</span>
      </button>
    </div>
  );
}

function ResponseField({ field, value, onChange }) {
  const copy = useLearningCopy();
  const fieldId = `assessment-${field.id}`;
  let control;
  if (field.type === 'multiple_choice') {
    control = (
      <div className="js-la-options">
        {(field.options || []).map((option) => (
          <label key={option}>
            <input type="radio" name={fieldId} value={option} checked={value === option} onChange={() => onChange(option)} />
            <span>{option}</span>
          </label>
        ))}
      </div>
    );
  } else if (field.type === 'ranking') {
    control = <RankingField field={field} value={value} onChange={onChange} />;
  } else if (field.type === 'table') {
    control = <TableField field={field} value={value} onChange={onChange} />;
  } else if (field.type === 'ordered_list') {
    control = (
      <textarea
        id={fieldId}
        rows="7"
        value={(Array.isArray(value) ? value : []).join('\n')}
        onChange={(event) => onChange(lines(event.target.value))}
        placeholder={copy('One item per line')}
      />
    );
  } else if (field.type === 'short_text' || field.type === 'calculation') {
    control = <input id={fieldId} value={value || ''} onChange={(event) => onChange(event.target.value)} />;
  } else {
    control = (
      <textarea
        id={fieldId}
        className={field.type === 'code' ? 'is-code' : ''}
        rows={field.type === 'code' ? 14 : 9}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  return (
    <section className="js-la-response-field">
      <label htmlFor={!['multiple_choice', 'ranking', 'table'].includes(field.type) ? fieldId : undefined}>
        <strong>{field.label}</strong>
        <span>{field.prompt}</span>
      </label>
      {control}
      {responseMinimum(field) > 1 && typeof value === 'string' && (
        <small className={value.length >= responseMinimum(field) ? 'is-met' : ''}>{value.length} {copy('entered')} · {responseMinimum(field)} {copy('minimum')}</small>
      )}
      {responseItemMinimum(field) > 1 && Array.isArray(value) && (
        <small className={value.length >= responseItemMinimum(field) ? 'is-met' : ''}>{value.length} {copy('items')} · {responseItemMinimum(field)} {copy('minimum')}</small>
      )}
    </section>
  );
}

function BriefPane({ task, className = '', onStart }) {
  const copy = useLearningCopy();
  const includesCode = (task.responseFields || []).some((field) => field.type === 'code');
  const tabs = [
    { id: 'task', label: copy('Task') },
    ...((task.constraints || []).length > 0 || (task.suppliedEvidence || []).length > 0
      ? [{ id: 'evidence', label: copy('Materials') }]
      : []),
    ...(task.scaffolding?.length > 0 || includesCode ? [{ id: 'approach', label: copy('Steps') }] : []),
    ...((task.rubric || []).length > 0 ? [{ id: 'success', label: copy('Checklist') }] : []),
  ];
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.id === activeTab));
  const tabBaseId = `brief-${String(task.taskType || 'task').toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className={`js-la-brief ${className}`.trim()}>
      <div className="js-la-brief-toolbar">
        <div className="js-scout-tabs js-la-brief-tabs" role="tablist" aria-label={copy('Exercise information')}>
          {tabs.map((tab) => (
            <button
              type="button"
              role="tab"
              id={`${tabBaseId}-tab-${tab.id}`}
              aria-controls={`${tabBaseId}-panel-${tab.id}`}
              aria-selected={activeTab === tab.id}
              className={activeTab === tab.id ? 'is-active' : ''}
              onClick={() => setActiveTab(tab.id)}
              key={tab.id}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {onStart && (
          <button type="button" className="js-lp-primary js-la-go-button" onClick={onStart}>
            <span>{copy('Start questions')}</span>
            <FiArrowRight className="js-icon--directional" aria-hidden="true" />
          </button>
        )}
      </div>
      <div
        className="js-la-brief-panel"
        role="tabpanel"
        id={`${tabBaseId}-panel-${activeTab}`}
        aria-labelledby={`${tabBaseId}-tab-${activeTab}`}
      >
        {activeTab === 'task' && (
          <section className="js-la-reading-section is-task">
            <span className="js-la-simulation-label">{copy('Practice task')}</span>
            <div className="js-la-reading-heading">
              <span>{copy('Your task')}</span>
              <h2>{copy('What to do')}</h2>
            </div>
            <p className="js-la-reading-lead">{task.scenario}</p>
            <aside className="js-la-reading-note">
              <FiTarget aria-hidden="true" />
              <p>{copy('Read the task. Then check Materials and Checklist before you answer.')}</p>
            </aside>
          </section>
        )}
        {activeTab === 'evidence' && (
          <section className="js-la-reading-section">
            <div className="js-la-reading-heading">
              <span>{copy('What you can use')}</span>
              <h2>{copy('Materials')}</h2>
              <p>{copy('Use the information on this page to complete the task.')}</p>
            </div>
            {(task.constraints || []).length > 0 && (
              <div className="js-la-reading-group">
                <h3>{copy('Rules')}</h3>
                <ul className="js-la-brief-list is-requirements">{task.constraints.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
            )}
            {(task.suppliedEvidence || []).length > 0 && (
              <div className="js-la-reading-group">
                <h3>{copy('Files and facts')}</h3>
                <div className="js-la-evidence-list">
                  {task.suppliedEvidence.map((item) => (
                    <article key={item.id}>
                      <strong>{item.title}</strong>
                      <MaterialContent content={item.content} />
                    </article>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}
        {activeTab === 'approach' && (
          <section className="js-la-reading-section">
            <div className="js-la-reading-heading">
              <span>{copy('Help if you need it')}</span>
              <h2>{copy('Steps to follow')}</h2>
              <p>{copy('These steps can help. You do not need to submit them.')}</p>
            </div>
            {task.scaffolding?.length > 0 && (
              <ol className="js-la-brief-list is-steps">{task.scaffolding.map((item) => <li key={item}>{item}</li>)}</ol>
            )}
            {includesCode && <p className="js-la-capability-note">{copy('We review the code or pseudocode you write. JOBSEEQR does not run it.')}</p>}
          </section>
        )}
        {activeTab === 'success' && (
          <section className="js-la-reading-section is-success">
            <div className="js-la-reading-heading">
              <span>{copy('Check your work')}</span>
              <h2>{copy('Before you submit')}</h2>
              <p>{copy('Make sure your answer includes each item below.')}</p>
            </div>
            <ul className="js-la-brief-list is-success">
              {task.rubric.map((item) => <li key={item.id}><FontAwesomeIcon icon={faCheck} aria-hidden="true" /><span><strong>{item.label}</strong>{item.description}</span></li>)}
            </ul>
          </section>
        )}
      </div>
      <nav className="js-la-brief-nav" aria-label={copy('Brief sections')}>
        <button type="button" className="js-lp-secondary" disabled={activeIndex === 0} onClick={() => setActiveTab(tabs[activeIndex - 1].id)}>
          <FiArrowLeft className="js-icon--directional" aria-hidden="true" /><span>{copy('Previous')}</span>
        </button>
        <span>{activeIndex + 1} {copy('of')} {tabs.length}</span>
        <button type="button" className="js-lp-secondary" disabled={activeIndex === tabs.length - 1} onClick={() => setActiveTab(tabs[activeIndex + 1].id)}>
          <span>{copy('Next')}</span><FiArrowRight className="js-icon--directional" aria-hidden="true" />
        </button>
      </nav>
    </div>
  );
}

function EvaluationPanel({ evaluation, title = 'Feedback' }) {
  const copy = useLearningCopy();
  if (!evaluation) return null;
  return (
    <section className="js-la-evaluation">
      <h2>{copy(title)}</h2>
      {evaluation.summary && <p>{evaluation.summary}</p>}
      <div className="js-la-criteria">
        {(evaluation.criteria || []).map((criterion) => (
          <article key={criterion.id} className={`is-${String(criterion.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
            <div>
              {criterion.status === 'Demonstrated' ? <FontAwesomeIcon icon={faCheck} aria-hidden="true" /> : <FiAlertCircle aria-hidden="true" />}
              <strong>{criterion.label}</strong>
              <span>{statusLabel(criterion.status, copy)}</span>
            </div>
            <p>{criterion.feedback}</p>
            {(criterion.evidence || []).map((evidence, index) => (
              <blockquote key={`${evidence.source}-${index}`}>
                <small>{copy(evidence.source === 'defense' ? 'Your answer' : 'Your work')}</small>
                {evidence.quote}
              </blockquote>
            ))}
          </article>
        ))}
      </div>
    </section>
  );
}

function Processing({ title, detail }) {
  return (
    <div className="js-la-processing" role="status">
      <FiRefreshCw className="is-spinning" aria-hidden="true" />
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}

export default function AssessmentWorkspace({
  user,
  stepNumber,
  exerciseIndex,
  generationId,
  onBack,
  onPassed,
}) {
  const copy = useLearningCopy();
  const { language } = useLanguage();
  const taskType = exerciseIndex === 1 ? 'Guided Practice' : 'Work Sample';
  const taskKey = exerciseIndex === 1 ? 'guidedPractice' : 'workSample';
  const [state, setState] = useState(null);
  const [responses, setResponses] = useState({});
  const [disclosure, setDisclosure] = useState({ usedAI: false, tools: '', purpose: '', verification: '' });
  const [saveStatus, setSaveStatus] = useState('idle');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [workspacePage, setWorkspacePage] = useState('brief');
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [briefExpanded, setBriefExpanded] = useState(false);
  const [defenseAnswer, setDefenseAnswer] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const hydratedRef = useRef(false);
  const completionHandledRef = useRef(false);

  const refresh = useCallback(async (retry = false) => {
    const next = await getLearningAssessment({ user, stepNumber, generationId, retry });
    setState(next);
    if (!hydratedRef.current && next?.drafts?.[taskKey]) {
      setResponses(next.drafts[taskKey].responses || {});
      if (taskKey === 'workSample') {
        setDisclosure((current) => ({ ...current, ...(next.drafts.workSample.aiUseDisclosure || {}) }));
      }
      hydratedRef.current = true;
    }
    return next;
  }, [generationId, stepNumber, taskKey, user]);

  useEffect(() => {
    let cancelled = false;
    setError('');
    refresh().catch((loadError) => {
      if (!cancelled) setError(copy('Could not load this exercise.'));
    });
    return () => { cancelled = true; };
  }, [copy, refresh]);

  const taskStatus = taskKey === 'guidedPractice' ? state?.guidedPracticeStatus : state?.workSampleStatus;
  const shouldPoll = ['queued', 'preparing'].includes(state?.status) || PROCESSING_STATUSES.has(taskStatus);
  useEffect(() => {
    if (!shouldPoll) return undefined;
    const id = window.setInterval(() => refresh().catch(() => {}), 2500);
    return () => window.clearInterval(id);
  }, [refresh, shouldPoll]);

  useEffect(() => {
    if (!state?.completionResult || completionHandledRef.current) return;
    if (state.completionResult.status !== 'passed') return;
    completionHandledRef.current = true;
    onPassed(state.completionResult);
  }, [onPassed, state?.completionResult]);

  useEffect(() => {
    const deadline = state?.currentAttempt?.defense?.deadlineAt;
    if (!deadline || taskStatus !== 'defense_in_progress') return undefined;
    const update = () => setSecondsLeft(Math.max(0, Math.floor((deadline - Date.now()) / 1000)));
    update();
    const id = window.setInterval(update, 1000);
    return () => window.clearInterval(id);
  }, [state?.currentAttempt?.defense?.deadlineAt, taskStatus]);

  const task = state?.challenge?.[taskKey];
  const responseFields = task?.responseFields || [];
  const questionCount = responseFields.length;
  const isReviewPage = activeQuestionIndex >= questionCount;
  const activeField = isReviewPage ? null : responseFields[activeQuestionIndex];
  const answeredCount = responseFields.filter((field) => hasAssessmentResponse(responses[field.id])).length;
  const incompleteRequiredFields = responseFields.filter((field) => field.required !== false && !hasAssessmentResponse(responses[field.id]));
  const allRequiredAnswersComplete = incompleteRequiredFields.length === 0;
  const assessmentProgress = questionCount > 0
    ? Math.round((answeredCount / questionCount) * 100)
    : 100;
  useEffect(() => {
    setActiveQuestionIndex((current) => Math.min(current, responseFields.length));
  }, [responseFields.length, taskKey]);
  const serializedDraft = useMemo(() => JSON.stringify({ responses, disclosure }), [disclosure, responses]);
  useEffect(() => {
    if (!hydratedRef.current || !task || state?.status !== 'ready') return undefined;
    setSaveStatus('pending');
    const id = window.setTimeout(() => {
      saveLearningAssessmentDraft({
        user,
        stepNumber,
        taskType,
        responses,
        aiUseDisclosure: disclosure,
      }).then(() => setSaveStatus('saved')).catch((saveError) => {
        setSaveStatus('error');
        setError(copy('Could not autosave this draft.'));
      });
    }, 900);
    return () => window.clearTimeout(id);
  }, [disclosure, serializedDraft, state?.status, stepNumber, task, taskType, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNow = async () => saveLearningAssessmentDraft({
    user,
    stepNumber,
    taskType,
    responses,
    aiUseDisclosure: disclosure,
  });

  const submit = async () => {
    setBusy('submit');
    setError('');
    try {
      await saveNow();
      if (taskKey === 'guidedPractice') await submitGuidedPractice({ user, stepNumber });
      else await submitWorkSample({ user, stepNumber });
      await refresh();
    } catch (submitError) {
      setError(copy('Could not submit this work.'));
    } finally {
      setBusy('');
    }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setBusy('upload');
    setError('');
    try {
      await uploadWorkSamplePdf({ user, stepNumber, file });
      await refresh();
    } catch (uploadError) {
      setError(language === 'ar' ? copy('Could not upload this PDF.') : uploadError.message || copy('Could not upload this PDF.'));
    } finally {
      setBusy('');
    }
  };

  const beginDefense = async () => {
    setBusy('defense');
    setError('');
    try {
      await startLearningAssessmentDefense({ user, stepNumber });
      await refresh();
    } catch (defenseError) {
      setError(copy('Could not start the final questions.'));
    } finally {
      setBusy('');
    }
  };

  const answerDefense = async () => {
    const question = state?.currentAttempt?.defense?.currentQuestion;
    if (!question) return;
    setBusy('answer');
    setError('');
    try {
      await answerLearningAssessmentDefense({
        user,
        stepNumber,
        questionId: question.id,
        answer: defenseAnswer,
      });
      setDefenseAnswer('');
      await refresh();
    } catch (answerError) {
      setError(copy('Could not send this answer.'));
    } finally {
      setBusy('');
    }
  };

  const retryPreparation = async () => {
    setBusy('preparation');
    setError('');
    try {
      await refresh(true);
    } catch (retryError) {
      setError(copy('Could not prepare the exercise again.'));
    } finally {
      setBusy('');
    }
  };

  if (!state || ['queued', 'preparing', 'missing'].includes(state.status)) {
    return (
      <section className="js-lp-panel js-la-workspace">
        <div className="js-la-top-row">
        <PlanControlSlot />
          <button type="button" className="js-lp-back" onClick={onBack}><FiArrowLeft className="js-icon--directional" aria-hidden="true" /> {copy('Back')}</button>
        </div>
        <Processing title={copy('Getting your exercise ready')} detail={copy('We are checking the task, materials, and questions.')} />
        {error && <p className="js-lp-error">{error}</p>}
      </section>
    );
  }

  if (state.status === 'error' || !task) {
    return (
      <section className="js-lp-panel js-la-workspace">
        <div className="js-la-top-row">
          <button type="button" className="js-lp-back" onClick={onBack}><FiArrowLeft className="js-icon--directional" aria-hidden="true" /> {copy('Back')}</button>
        </div>
        <div className="js-la-system-error">
          <FiAlertCircle aria-hidden="true" />
          <h2>{copy('We could not prepare this exercise')}</h2>
          <p>{state.error || error}</p>
          <button type="button" className="js-lp-primary" onClick={retryPreparation} disabled={busy === 'preparation'}>
            <FiRefreshCw aria-hidden="true" />
            <span>{copy(busy === 'preparation' ? 'Trying again...' : 'Try again')}</span>
          </button>
        </div>
      </section>
    );
  }

  const evaluation = taskKey === 'guidedPractice'
    ? state.guidedEvaluation
    : state.currentAttempt?.evaluation || (['needs_revision', 'not_assessable'].includes(taskStatus) ? state.currentAttempt?.artifactEvaluation : null);
  const defense = state.currentAttempt?.defense;
  const readOnly = ['evaluating', 'evaluating_artifact', 'defense_in_progress', 'evaluating_final', 'passed'].includes(taskStatus);

  return (
    <section className="js-lp-panel js-la-workspace">
      <div className="js-la-top-row">
        <button type="button" className="js-lp-back" onClick={onBack}><FiArrowLeft className="js-icon--directional" aria-hidden="true" /> {copy('Back')}</button>
      </div>

      <header className="js-la-header">
        <div className="js-la-header-copy">
          <span>{copy(taskKey === 'workSample' ? 'Final task' : 'Practice')}</span>
          <h1>{task.title}</h1>
          <p><FiClock aria-hidden="true" /> {copy('About {minutes} minutes', { minutes: task.estimatedMinutes })}</p>
        </div>
        <div className="js-la-save-state" aria-live="polite">
          {saveStatus === 'pending' && copy('Saving...')}
          {saveStatus === 'saved' && <><FontAwesomeIcon icon={faCheck} aria-hidden="true" /> {copy('Saved')}</>}
          {saveStatus === 'error' && copy('Save failed')}
        </div>
      </header>

      {task.deviceSupport === 'desktop_recommended' && (
        <div className="js-la-device-note"><FiMonitor aria-hidden="true" /><span>{copy('A computer is best for this task. You can still view your work and feedback on your phone.')}</span></div>
      )}

      {workspacePage === 'brief' ? (
        <div className="js-la-intro js-la-question-view">
          <BriefPane
            task={task}
            onStart={() => {
              setActiveQuestionIndex(0);
              setWorkspacePage('assessment');
            }}
          />
        </div>
      ) : (
        <main className="js-la-assessment-page js-la-question-view">
          <section className={`js-la-question-summary ${briefExpanded ? 'is-expanded' : 'is-collapsed'}`}>
            <div className="js-la-question-summary-copy">
              <span>{copy('Task and materials')}</span>
              <p>{copy('Open the task, materials, steps, or checklist at any time.')}</p>
            </div>
            {briefExpanded && <BriefPane task={task} className="is-question-reference" />}
            <div className="js-la-question-toggle">
              <button
                type="button"
                aria-expanded={briefExpanded}
                onClick={() => setBriefExpanded((expanded) => !expanded)}
              >
                <FiFileText aria-hidden="true" />
                <span>{copy(briefExpanded ? 'Close task & materials' : 'View task & materials')}</span>
                {briefExpanded ? <FiArrowUp aria-hidden="true" /> : <FiArrowDown aria-hidden="true" />}
              </button>
            </div>
          </section>

          <section className="js-la-answer-area" aria-label={copy('Assessment questions')}>
          <div className="js-la-assessment-progress" aria-label={copy('Assessment progress')}>
            <div>
              <span>{isReviewPage ? copy('Final step') : copy('Question {current} of {total}', { current: activeQuestionIndex + 1, total: questionCount })}</span>
              <strong>
                {isReviewPage
                  ? allRequiredAnswersComplete
                    ? copy('All required answers complete')
                    : copy('{count} required answers missing', { count: incompleteRequiredFields.length })
                  : copy('{answered} of {total} answered', { answered: answeredCount, total: questionCount })}
              </strong>
            </div>
            <div className="js-la-assessment-progress-track" aria-hidden="true"><span style={{ width: `${assessmentProgress}%` }} /></div>
          </div>

          <div className="js-la-question-card">
            {!isReviewPage && activeField && (
              <fieldset className="js-la-response-list" disabled={readOnly}>
                <ResponseField
                  key={activeField.id}
                  field={activeField}
                  value={responses[activeField.id]}
                  onChange={(value) => !readOnly && setResponses((current) => ({ ...current, [activeField.id]: value }))}
                />
              </fieldset>
            )}

            {isReviewPage && (
              <section className="js-la-review-step">
                <span>{copy(allRequiredAnswersComplete ? 'FINAL CHECK' : 'ACTION NEEDED')}</span>
                <h2>{copy('Review your answers')}</h2>
                <p>
                  {allRequiredAnswersComplete
                    ? copy('Check every response below, then submit when you are satisfied.')
                    : copy('Complete the required questions marked below before submitting.')}
                </p>
              </section>
            )}

            {isReviewPage && (
              <section className="js-la-review-answers" aria-label={copy('Review your answers')}>
                <h3>{copy('Your answers')}</h3>
                <fieldset className="js-la-review-fields" disabled={readOnly}>
                  {responseFields.map((field) => {
                    const incomplete = field.required !== false && !hasAssessmentResponse(responses[field.id]);
                    return (
                      <div className={incomplete ? 'is-incomplete' : ''} key={field.id}>
                        {incomplete && <span className="js-la-review-missing">{copy('Required answer missing')}</span>}
                        <ResponseField
                          field={field}
                          value={responses[field.id]}
                          onChange={(value) => !readOnly && setResponses((current) => ({ ...current, [field.id]: value }))}
                        />
                      </div>
                    );
                  })}
                </fieldset>
              </section>
            )}

          {isReviewPage && taskKey === 'workSample' && task.acceptsPdfEvidence === true && (
            <section className="js-la-supporting-evidence">
              <h2>{copy('Add a PDF (optional)')}</h2>
              <p>{copy('Your file stays private. We only review the text we can read from it.')}</p>
              <label className={readOnly || busy === 'upload' ? 'is-disabled' : ''}>
                <FiUpload aria-hidden="true" />
                <span>{copy(busy === 'upload' ? 'Uploading...' : 'Choose PDF')}</span>
                <input type="file" accept="application/pdf" disabled={readOnly || busy === 'upload'} onChange={(event) => uploadPdf(event.target.files?.[0])} />
              </label>
              {state.drafts?.workSample?.pdfEvidence && (
                <div><FiFileText aria-hidden="true" /><span>{state.drafts.workSample.pdfEvidence.name}</span><small>{state.drafts.workSample.pdfEvidence.pages} {copy('pages')}</small></div>
              )}
            </section>
          )}

          {isReviewPage && taskKey === 'workSample' && (
            <section className="js-la-disclosure">
              <h2>{copy('Did you use AI?')}</h2>
              <label className="js-la-check"><input type="checkbox" checked={disclosure.usedAI} disabled={readOnly} onChange={(event) => setDisclosure((current) => ({ ...current, usedAI: event.target.checked }))} /><span>{copy('Yes, I used an AI tool')}</span></label>
              {disclosure.usedAI && <label><span>{copy('Which tools?')}</span><input value={disclosure.tools} disabled={readOnly} onChange={(event) => setDisclosure((current) => ({ ...current, tools: event.target.value }))} /></label>}
              {disclosure.usedAI && <label><span>{copy('How did AI help?')}</span><textarea rows="4" value={disclosure.purpose} disabled={readOnly} onChange={(event) => setDisclosure((current) => ({ ...current, purpose: event.target.value }))} /></label>}
              <label><span>{copy('How did you check the work?')}</span><textarea rows="4" value={disclosure.verification} disabled={readOnly} onChange={(event) => setDisclosure((current) => ({ ...current, verification: event.target.value }))} placeholder={copy('List the checks you made and anything you are still unsure about.')} /></label>
            </section>
          )}

          {['evaluating', 'evaluating_artifact'].includes(taskStatus) && <Processing title={copy('Checking your work')} detail={copy('We are reviewing your answers and preparing feedback.')} />}
          {taskStatus === 'evaluating_final' && <Processing title={copy('Checking your final answers')} detail={copy('We are comparing two reviews before we show your result.')} />}

          {taskKey === 'workSample' && taskStatus === 'awaiting_defense' && (
            <section className="js-la-defense-intro">
              <h2>{copy('Your work has been reviewed')}</h2>
              <p>{copy('Next, answer three short questions about your work. You have 12 minutes. You cannot change an answer after you send it.')}</p>
              <button type="button" className="js-lp-primary" disabled={busy === 'defense'} onClick={beginDefense}>{copy(busy === 'defense' ? 'Starting...' : 'Start questions')}</button>
            </section>
          )}

          {taskKey === 'workSample' && taskStatus === 'defense_in_progress' && defense?.currentQuestion && (
            <section className="js-la-defense">
              <div><span>{copy('Question {current} of {total}', { current: defense.currentQuestion.number, total: 3 })}</span><strong><FiClock aria-hidden="true" /> {clock(secondsLeft)}</strong></div>
              <h2>{defense.currentQuestion.text}</h2>
              <textarea rows="8" value={defenseAnswer} onChange={(event) => setDefenseAnswer(event.target.value)} />
              <button type="button" className="js-lp-primary" disabled={busy === 'answer' || defenseAnswer.trim().length < 40 || secondsLeft <= 0} onClick={answerDefense}>
                <FiSend aria-hidden="true" /><span>{copy(busy === 'answer' ? 'Submitting...' : 'Submit answer')}</span>
              </button>
            </section>
          )}

          <EvaluationPanel evaluation={evaluation} title={taskKey === 'guidedPractice' ? 'Feedback' : 'Final result'} />
          {taskStatus === 'passed' && <div className="js-la-passed"><FontAwesomeIcon icon={faCheck} aria-hidden="true" /><strong>{copy('You passed every required item.')}</strong></div>}

          {error && <p className="js-lp-error">{error}</p>}

          <nav className="js-la-question-nav" aria-label={copy('Assessment questions')}>
            <button
              type="button"
              className="js-lp-secondary"
              disabled={activeQuestionIndex === 0}
              onClick={() => setActiveQuestionIndex((current) => Math.max(0, current - 1))}
            >
              <FiArrowLeft className="js-icon--directional" aria-hidden="true" /><span>{copy('Previous')}</span>
            </button>
            {!isReviewPage && (
              <button type="button" className="js-lp-primary" onClick={() => setActiveQuestionIndex((current) => Math.min(questionCount, current + 1))}>
                <span>{copy(activeQuestionIndex === questionCount - 1 ? 'Review answers' : 'Next question')}</span><FiArrowRight className="js-icon--directional" aria-hidden="true" />
              </button>
            )}
          </nav>

          {!readOnly && isReviewPage && !['awaiting_defense'].includes(taskStatus) && (
            <div className="js-lp-actions is-right js-la-submit-action">
              <button type="button" className="js-lp-primary" onClick={submit} disabled={busy === 'submit' || !allRequiredAnswersComplete}>
                <span>{copy(busy === 'submit' ? 'Submitting...' : taskKey === 'guidedPractice' ? 'Send for feedback' : taskStatus === 'needs_revision' ? 'Send changes' : 'Submit final task')}</span>
                <FiSend aria-hidden="true" />
              </button>
            </div>
          )}
          </div>
          </section>
        </main>
      )}

      {workspacePage === 'assessment' && state.attemptHistory?.length > 0 && taskKey === 'workSample' && (
        <footer className="js-la-history">
          <h2>{copy('Attempt history')}</h2>
          {state.attemptHistory.map((attempt) => <span key={attempt.attemptNumber}>{copy('Attempt')} {attempt.attemptNumber}: {String(attempt.status || '').replace(/_/g, ' ')}</span>)}
        </footer>
      )}
    </section>
  );
}
